/**
 * Lazy-loads the Reviews.io carousel-inline-iframeless widget when a
 * [data-reviews-carousel-widget] element scrolls into view.
 *
 * Shared helpers (rating extractor, /5 injection, cleanup) komen uit
 * assets/reviews-widgets-shared.js via window.RWS.
 */
(function () {
  'use strict';

  var BUILD_SCRIPT_SRC = 'https://widget.reviews.io/carousel-inline-iframeless/dist.js';
  var STYLESHEETS = [
    'https://assets.reviews.io/css/widgets/carousel-widget.css',
    'https://assets.reviews.io/iconfont/reviewsio-icons/style.css'
  ];
  /* Was 400px → te krap voor cart-drawer + announcement-bar use cases.
     Met 1200px begint init voor widgets die binnen ~1 viewport-hoogte
     boven of onder de fold liggen. Combined met de warmUp (zie onder)
     is dat genoeg voor instant-rendering bij scroll/drawer-open. */
  var ROOT_MARGIN = '1200px 0px';

  var buildScriptPromise = null;

  function injectStylesheets() {
    STYLESHEETS.forEach(function (href) {
      if (document.querySelector('link[rel="stylesheet"][href="' + href + '"]')) return;
      var link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      document.head.appendChild(link);
    });
  }

  function loadBuildScript() {
    if (buildScriptPromise) return buildScriptPromise;

    buildScriptPromise = new Promise(function (resolve, reject) {
      if (typeof window.carouselInlineWidget === 'function') {
        resolve();
        return;
      }
      var existing = document.querySelector('script[data-rcw-build]');
      if (existing) {
        existing.addEventListener('load', function () { resolve(); });
        existing.addEventListener('error', reject);
        return;
      }
      var script = document.createElement('script');
      script.src = BUILD_SCRIPT_SRC;
      script.async = true;
      script.setAttribute('data-rcw-build', '');
      script.onload = function () { resolve(); };
      script.onerror = reject;
      document.head.appendChild(script);
    });

    return buildScriptPromise;
  }

  /* Snapshot de gerenderde mount-content naar localStorage zodat een
     volgende pagina-navigatie de widget INSTANT kan tonen (zie de
     cache-restore inline script in reviews-carousel-widget-block.liquid).
     Wacht ~3s na init zodat de Reviews.io API-fetch + DOM render klaar
     zijn voordat we de snapshot maken.

     Cache key includeert de config-hash zodat een setting-wijziging
     (review_type, max_reviews, heading) een nieuwe key krijgt en de
     oude cache niet meer wordt gehit. */
  function cacheSnapshot(el, configId, mountId) {
    // Niet cachen in theme-editor design mode — settings veranderen
    // daar live en cache-restore zou de nieuwe instellingen verbergen.
    if (window.Shopify && window.Shopify.designMode) return;
    setTimeout(function () {
      try {
        var mount = document.getElementById(mountId);
        if (!mount) return;
        var html = mount.innerHTML;
        if (!html || html.length < 100) return; // niets nuttigs om te cachen
        var configHash = (el && el.dataset.configHash) || '';
        localStorage.setItem('__rcw_cache_' + configId + '_' + configHash, JSON.stringify({
          t: Date.now(),
          html: html
        }));
      } catch (e) { /* localStorage vol/uit → silently skip */ }
    }, 3000);
  }

  function initWidget(el) {
    if (el.dataset.rcwInitialized === 'true') return;
    el.dataset.rcwInitialized = 'true';

    var configId = el.dataset.configId;
    var mountId = el.dataset.mountId;
    var config = window.__rcwConfigs && window.__rcwConfigs[configId];

    if (!config || !mountId) {
      console.warn('[reviews-carousel-widget] missing config or mountId', configId);
      return;
    }

    /* Cache-restore heeft de mount al gevuld met de cached HTML
       (compact context — announcement-bar). De externe Reviews.io
       widget hoeft hier niets meer te doen; we draaien alleen onze
       enhancer over de cached DOM voor consistency met onze styling. */
    if (el.dataset.rcwFromCache === 'true') {
      attachEnhancer(el);
      return;
    }

    injectStylesheets();
    loadBuildScript()
      .then(function () {
        if (typeof window.carouselInlineWidget !== 'function') {
          throw new Error('carouselInlineWidget global missing after dist.js load');
        }
        new window.carouselInlineWidget(mountId, config);
        el.classList.add('reviews-carousel-widget--loaded');
        attachEnhancer(el);
        cacheSnapshot(el, configId, mountId);
      })
      .catch(function (err) {
        console.error('[reviews-carousel-widget] init failed', err);
        el.classList.add('reviews-carousel-widget--error');
      });
  }

  /**
   * Per-card enhancement:
   *  1. Restructure: bouw .rcw-meta-box bovenaan met verified-pill + naam + location + recommends
   *  2. Splits "City, Country, X ago" in losse location + date
   *  3. Vertaal "I recommend this product" → "Ik raad dit product aan"
   *  4. Injecteer "X/5" naast review-stars (via shared helper)
   *  5. Restructure side-header "4,8/5 ★★★★★" + "Gebaseerd op X recensies"
   *
   * Cleanup: MutationObserver disconnect bij section-unload.
   */
  function attachEnhancer(el) {
    if (!window.RWS) return; // shared.js niet geladen

    function injectRating(starGroup) {
      // Skip side-header overall stars — die behandelen we apart in fixSideHeader
      if (starGroup.closest('.CarouselWidget__header')) return;
      window.RWS.injectRating(starGroup, 'rcw-rating-num');
    }

    function restructureCard(card) {
      if (card.dataset.rcwRestructured === 'true') return;
      var inner = card.querySelector('.item__inner');
      if (!inner) return;

      var authorNameEl = inner.querySelector('.cssVar-authorName');
      var badges = inner.querySelectorAll('.R-BadgeElement');
      var verifiedBadge = badges[0] || null;
      var recommendsBadge = badges[1] || null;
      var dateLocationEl = inner.querySelector('.R-flex-between-xxs .u-textRight--all, .R-flex-between-xxs .R-TextBody');

      if (!authorNameEl && !verifiedBadge && !dateLocationEl) return;
      card.dataset.rcwRestructured = 'true';

      // Splits "City, Country, X ago"
      var locationText = '';
      var dateText = '';
      if (dateLocationEl) {
        var fullText = (dateLocationEl.textContent || '').trim();
        var parts = fullText.split(',').map(function (p) { return p.trim(); }).filter(Boolean);
        if (parts.length >= 3) {
          dateText = parts[parts.length - 1];
          locationText = parts.slice(0, -1).join(', ');
        } else if (parts.length === 2) {
          if (/ago|geleden|hour|minut|day|week|month|year|uur|dag|min|maand|jaar/i.test(parts[1])) {
            locationText = parts[0];
            dateText = parts[1];
          } else {
            locationText = fullText;
          }
        } else {
          dateText = fullText;
        }
      }

      // Vertaal recommends → Nederlands
      if (recommendsBadge) {
        var recText = recommendsBadge.querySelector('.cssVar-badgeElement__text, .R-BadgeElement__text');
        if (recText && /recommend|aanbeve|raad|aanrad/i.test(recText.textContent || '')) {
          recText.textContent = 'Ik raad dit product aan';
        }
      }

      // Bouw grey meta-box bovenop card
      var metaBox = document.createElement('div');
      metaBox.className = 'rcw-meta-box';

      if (verifiedBadge) {
        verifiedBadge.classList.add('rcw-verified-pill');
        metaBox.appendChild(verifiedBadge);
      }

      if (authorNameEl) {
        var nameWrap = document.createElement('div');
        nameWrap.className = 'rcw-meta-name';
        nameWrap.textContent = (authorNameEl.textContent || '').trim();
        metaBox.appendChild(nameWrap);
        var oldHeading = authorNameEl.closest('.R-TextHeading');
        if (oldHeading) oldHeading.style.display = 'none';
      }

      if (locationText) {
        var locWrap = document.createElement('div');
        locWrap.className = 'rcw-meta-location';
        locWrap.textContent = locationText;
        metaBox.appendChild(locWrap);
      }

      if (recommendsBadge && (recommendsBadge.textContent || '').trim().length > 0) {
        recommendsBadge.classList.add('rcw-recommends-text');
        metaBox.appendChild(recommendsBadge);
      }

      inner.insertBefore(metaBox, inner.firstChild);

      // Date-only in bottom-right
      if (dateLocationEl && dateText) {
        dateLocationEl.textContent = dateText;
      } else if (dateLocationEl && !dateText) {
        dateLocationEl.style.display = 'none';
      }
    }

    function fixSideHeader(scope) {
      var headerInner = scope.querySelector('.CarouselWidget__header .header__inner');
      if (!headerInner) return;

      // /5 toevoegen aan "4,8" → "4,8/5"
      var numSpans = headerInner.querySelectorAll('.cssVar-subheading__number');
      Array.prototype.forEach.call(numSpans, function (span) {
        if (span.dataset.rcwSuffixed === '1') return;
        var text = (span.textContent || '').trim();
        if (/^[\d.,]+$/.test(text)) {
          var num = parseFloat(text.replace(',', '.'));
          if (num > 0 && num <= 5) {
            span.dataset.rcwSuffixed = '1';
            span.textContent = text + '/5';
          }
        }
      });

      // Restructure rating-row + count-text — only once per header
      if (headerInner.dataset.rcwRestructured === 'true') return;

      var stars = headerInner.querySelector('.R-RatingStars');
      var subheadings = headerInner.querySelectorAll('.cssVar-header__subheading');
      if (!stars || subheadings.length < 1) return;

      var firstSub = subheadings[0];
      var firstNum = firstSub.querySelector('.cssVar-subheading__number');
      if (!firstNum) return;
      var ratingText = (firstNum.textContent || '').trim();
      if (!ratingText) return;

      headerInner.dataset.rcwRestructured = 'true';

      // Bouw rating row "4,8/5 ★★★★★"
      var ratingRow = document.createElement('div');
      ratingRow.className = 'rcw-side-rating-row';

      var bigNum = document.createElement('span');
      bigNum.className = 'rcw-side-rating-num';
      bigNum.textContent = ratingText;
      ratingRow.appendChild(bigNum);
      ratingRow.appendChild(stars); // moves stars

      // Plaats rij na UITSTEKEND heading
      var textHeadingEl = headerInner.querySelector('.R-TextHeading');
      if (textHeadingEl && textHeadingEl.nextSibling) {
        headerInner.insertBefore(ratingRow, textHeadingEl.nextSibling);
      } else if (textHeadingEl) {
        textHeadingEl.parentNode.appendChild(ratingRow);
      } else {
        headerInner.insertBefore(ratingRow, headerInner.firstChild);
      }

      // Verberg "4,8/5 Gemiddeld" tekst (cijfer staat al in nieuwe rij)
      var firstSubWrapper = firstSub.closest('.R-TextBody') || firstSub;
      firstSubWrapper.style.display = 'none';

      // "15.696 Recensies" → "Gebaseerd op 15.696 recensies"
      if (subheadings.length >= 2) {
        var secondSub = subheadings[1];
        var countNum = secondSub.querySelector('.cssVar-subheading__number');
        if (countNum) {
          var count = (countNum.textContent || '').trim();
          if (count) {
            secondSub.innerHTML = 'Gebaseerd op <strong>' + count + '</strong> recensies';
          }
        }
      }
    }

    function enhanceAll(scope) {
      var cards = scope.querySelectorAll('.R-ReviewsList__item');
      Array.prototype.forEach.call(cards, function (card) {
        restructureCard(card);
        var stars = card.querySelector('.R-RatingStars');
        if (stars) injectRating(stars);
      });
      fixSideHeader(scope);
    }

    // Initial pass — Reviews.io carousel async rendert (~500-1000ms typisch)
    var initialTimer = setTimeout(function () { enhanceAll(el); }, 500);

    // MutationObserver vangt re-renders bij auto-scroll, filter, pagination
    var mo = new MutationObserver(function () { enhanceAll(el); });
    mo.observe(el, { childList: true, subtree: true });

    // Cleanup op shopify:section:unload (Theme Editor section toggle)
    window.RWS.registerCleanup(el, function () {
      clearTimeout(initialTimer);
      mo.disconnect();
    });
  }

  function observe(el) {
    if (!('IntersectionObserver' in window)) {
      initWidget(el);
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          io.disconnect();
          initWidget(el);
        }
      });
    }, { rootMargin: ROOT_MARGIN, threshold: 0.01 });
    io.observe(el);
  }

  function bootAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var nodes = scope.querySelectorAll('[data-reviews-carousel-widget]');
    Array.prototype.forEach.call(nodes, function (el) {
      if (el.dataset.rcwObserved === 'true') return;
      el.dataset.rcwObserved = 'true';
      observe(el);
    });
  }

  /**
   * Warm-up + eager-mount:
   *  1) Trigger build.js + stylesheets fetch zodra de browser idle is.
   *  2) Zodra die binnen zijn, mount ALLE widgets op de pagina (ook
   *     onzichtbare in de cart-drawer of cart-page below fold). Dit
   *     skipt de IntersectionObserver-wait — wat de user voelde als
   *     "drawer-opent dan pas laden de reviews".
   *
   * Werkt prima omdat:
   *  - De Reviews.io API-call gebeurt sowieso één keer per store_id.
   *    Tweede/derde widget-mount hit de browser-cache.
   *  - Reviews.io's carousel widget mount netjes in display:none
   *    containers; layout wordt pas berekend zodra de drawer opent.
   *  - dist.js (de zware bundle) wordt singleton geladen via
   *    `buildScriptPromise`.
   *
   * Niet runnen op pages zónder widgets — zonde van bandwidth.
   */
  function warmUp() {
    var widgets = document.querySelectorAll('[data-reviews-carousel-widget]');
    if (!widgets.length) return;

    injectStylesheets();
    loadBuildScript()
      .then(function () {
        // Eager-init alles — dist.js is binnen, het is goedkoop nu.
        Array.prototype.forEach.call(widgets, function (el) {
          // Markeer als observed zodat de bestaande IntersectionObserver
          // in bootAll() dit element niet nog eens probeert te initiëren.
          el.dataset.rcwObserved = 'true';
          initWidget(el);
        });
      })
      .catch(function () { /* silent — IO-pad logt 't bij echte init-fout */ });
  }

  function scheduleWarmUp() {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(warmUp, { timeout: 1500 });
    } else {
      // Safari fallback — 500ms na DOMContentLoaded is genoeg om de
      // critical render path niet te verstoren.
      setTimeout(warmUp, 500);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      bootAll(document);
      scheduleWarmUp();
    });
  } else {
    bootAll(document);
    scheduleWarmUp();
  }

  document.addEventListener('shopify:section:load', function (e) {
    bootAll(e.target || document);
  });
  document.addEventListener('shopify:section:select', function (e) {
    bootAll(e.target || document);
  });
})();
